import { Megaphone, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRoleStore } from "@/shared/store/role";
import { useBloggersWithPlacements } from "@/shared/api/wb";
import { cn } from "@/shared/lib/cn";

/** Prominent role toggle, always visible in the Topbar regardless of route —
 * flips between the advertiser cabinet and the blogger cabinet for the same
 * underlying session/data (see RoleStartupModal + blogger_portal.py). */
export function RoleSwitcher() {
  const role = useRoleStore((s) => s.role);
  const setRole = useRoleStore((s) => s.setRole);
  const activeBloggerId = useRoleStore((s) => s.activeBloggerId);
  const setActiveBloggerId = useRoleStore((s) => s.setActiveBloggerId);
  const { data: bloggers } = useBloggersWithPlacements();
  const navigate = useNavigate();

  function toggle(next: "advertiser" | "blogger") {
    if (next === role) return;
    if (next === "blogger" && !activeBloggerId) {
      setActiveBloggerId(bloggers?.[0]?.id ?? null);
    }
    setRole(next);
    navigate(next === "blogger" ? "/blogger-cabinet/inbox" : "/account/projects");
  }

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <div className="flex items-center h-9 rounded-pill bg-ink-6 p-0.5">
        <button
          onClick={() => toggle("advertiser")}
          className={cn(
            "inline-flex items-center gap-1.5 h-8 px-3 rounded-pill text-[12.5px] font-display font-semibold transition-colors",
            role === "advertiser" ? "bg-white text-ink shadow-sm" : "text-ink-40 hover:text-ink",
          )}
        >
          <Megaphone size={13} /> Рекламодатель
        </button>
        <button
          onClick={() => toggle("blogger")}
          className={cn(
            "inline-flex items-center gap-1.5 h-8 px-3 rounded-pill text-[12.5px] font-display font-semibold transition-colors",
            role === "blogger" ? "bg-white text-ink shadow-sm" : "text-ink-40 hover:text-ink",
          )}
        >
          <UserRound size={13} /> Блогер
        </button>
      </div>

      {role === "blogger" && (
        <div className="relative">
          <select
            value={activeBloggerId ?? ""}
            onChange={(e) => setActiveBloggerId(e.target.value || null)}
            className="h-9 pl-3 pr-7 rounded-pill border border-ink-12 bg-white text-[12.5px] font-display font-semibold outline-none focus:border-ink appearance-none cursor-pointer max-w-[180px]"
            title="Смотрим как"
          >
            {(bloggers ?? []).length === 0 && <option value="">Нет заявок</option>}
            {(bloggers ?? []).map((b) => (
              <option key={b.id} value={b.id}>{b.name}{b.new_count > 0 ? ` · ${b.new_count} новых` : ""}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
